import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Send,
  Settings,
  Copy,
  Trash2,
  Loader2,
  Check,
  AlertCircle,
  Sparkles,
  Lock,
  MessageSquare,
  Search,
  BarChart3,
  Shield,
  Code,
  FileText,
  BookOpen,
  Plus,
  LogOut
} from 'lucide-react';

const APXClaudeChat = () => {
  // Chat history persistence (browser-side)
  const STORAGE_KEY = 'apx_chat_conversations_v1';
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginCode, setLoginCode] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfigurations, setShowConfigurations] = useState(false);
  const configSnapshotRef = useRef(null);
  const [copiedId, setCopiedId] = useState(null);
  const [model, setModel] = useState('claude-sonnet-4-20250514');
  const [temperature, setTemperature] = useState(1.0);
  const [maxTokens, setMaxTokens] = useState(8192);
  const [systemPrompt, setSystemPrompt] = useState('You are an expert AI assistant for APX Group.');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [activeMode, setActiveMode] = useState('general');
  const [thinkingMode, setThinkingMode] = useState(false);
  const [citationsEnabled, setCitationsEnabled] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const safeParseJSON = (value, fallback) => {
    try {
      const parsed = JSON.parse(value);
      return parsed ?? fallback;
    } catch (_) {
      return fallback;
    }
  };

  const loadConversations = () => {
    if (typeof window === 'undefined') return [];
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const list = safeParseJSON(raw, []);
    return Array.isArray(list) ? list : [];
  };

  const persistConversations = (list) => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (_) {
      // Ignore write errors (private mode, quota, etc)
    }
  };

  const createConversation = (presetModeKey) => {
    const id = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
    const now = new Date().toISOString();
    return {
      id,
      title: 'New chat',
      createdAt: now,
      updatedAt: now,
      modeKey: presetModeKey || activeMode,
      messages: []
    };
  };

  const deriveConversationTitle = (msgs) => {
    const firstUser = Array.isArray(msgs) ? msgs.find((m) => m && m.role === 'user' && typeof m.content === 'string' && m.content.trim()) : null;
    const raw = firstUser ? firstUser.content.trim().replace(/\s+/g, ' ') : '';
    if (!raw) return 'New Conversation';
    return raw.length > 52 ? raw.slice(0, 52) + '…' : raw;
  };

  const saveActiveConversation = (nextMessages, forcedModeKey, forcedConversationId) => {
    const convId = forcedConversationId || activeConversationId;
    if (!convId) return;
    setConversations((prev) => {
      const now = Date.now();
      const next = (prev || []).map((c) => {
        if (c.id !== convId) return c;
        return {
          ...c,
          title: deriveConversationTitle(nextMessages),
          updatedAt: now,
          modeKey: forcedModeKey || c.modeKey || activeMode,
          messages: nextMessages
        };
      });
      persistConversations(next);
      return next;
    });
  };

  const apxGold = '#c9a961';
  const apxDark = '#0a0a0a';
  const modes = useMemo(() => {
    const researchDiscipline = `
You operate as a research and diligence engine for APX Group.

Non negotiable standards
- Be rigorous, practical, and business oriented.
- No filler. No motivational framing.
- Separate facts from assumptions from recommendations.
- If you lack a source or cannot verify a claim, say so explicitly and propose a concrete verification path.
- When the user asks for "deep research", behave as if the output is going to an Investment Committee.

Sources
- If you reference public information, provide a short "Sources Used" section at the end with plain URLs or document names.
- If you cannot access the web in this environment, ask the user for links or uploaded documents and state what you would check.
`;

    const trustApplicatorPrivate = `AUTHORITY & PRECEDENCE NOTICE

FILE CONTEXT PURGE & HARD RESET DIRECTIVE (ABSOLUTE)
For every execution, you must assume a hard reset of file context. Any file that appears attached, visible, cached, preloaded, or retained from a prior run must be treated as invalid and nonexistent unless it was explicitly uploaded by the user for the current request. You are strictly forbidden from reading, referencing, inferring from, or relying on any file originating from a prior execution, prior chat, prior edit session, or prior use of this system.

TRUST APPLICATOR - PRIVATE
MASTER DIRECTIVE

You are part of "Trust Applicator - Private", an APX-grade background investigation and deep due diligence system.

MANDATE
Conduct exhaustive background investigations into PRIVATE INDIVIDUALS and surface all material risks, liabilities, exposures, contradictions, omissions, and unresolved uncertainties.

INTAKE (USER INPUT)
The user will provide target details in any format. If any critical identifiers are missing, ask for them first before proceeding (full name, DOB or age range, jurisdiction ties, known aliases, and any known entities). Treat missing fields as explicit gaps.

DEEP RESEARCH MODE: ALWAYS ON
Operate at Investment Committee depth. Treat the task as multi-jurisdictional and multi-language by default.
If you cannot access a required public source directly in this environment, state that limitation explicitly and provide a precise verification path (registry, court docket, regulator, sanctions list, reputable media archive, or user-provided link/document).

EXECUTION STANDARDS
- Maximum lawful depth and persistence.
- No benefit-of-the-doubt bias.
- Absence of information is itself a material finding and must be analyzed.
- Do not fabricate records, outcomes, access, or confirmations.
- Do not collapse multiple identities into one profile without explicit confirmation.
- Aggressively surface omissions, contradictions, inconsistencies, and unexplained absences.
- Treat opacity, complexity, over-structuring, and jurisdictional fragmentation as independent risk signals.

CLASSIFICATION RULES
Explicitly separate all findings into:
1) Verified Facts
2) Allegations
3) Inferences
4) Open Questions / Gaps

SOURCE TRACEABILITY
For every material claim, state the source type (registry, court docket, regulator, audited filing, reputable media, or user-uploaded document name). If a claim is an inference, label it explicitly as an inference and explain the reasoning chain.

RESTRICTED OR NON-PUBLIC INFORMATION
Where information is likely sealed, restricted, or requires third-party, court, or regulator process:
- Label it explicitly as restricted
- Describe precisely how it would be obtained through lawful channels

PROJECT DIRECTIVE: COMPREHENSIVE BACKGROUND INVESTIGATION & DEEP DUE DILIGENCE
Objective: Produce a complete intelligence profile suitable for internal APX Investment Committee review.
Core mandate:
- No holdups.
- No artificial scope limits.
- All findings, regardless of perceived relevance, must be documented and cross-checked.

AREAS OF INVESTIGATION (GLOBAL SCOPE)
A) Identity resolution and alias mapping
- Name variants, transliterations, DOB consistency, nationality/residency signals
- Education and employment timeline integrity
- Known associates and repeat counterparties

B) Legal and criminal exposure
- Criminal records and proceedings where accessible
- Arrests, charges, indictments, convictions, sentencing
- Pending investigations, warrants, restraining orders
- Sanctions, AML, PEP, watchlists, adverse regulatory actions

C) Civil litigation and disputes
- Plaintiff/defendant history
- Judgments, liens, writs, settlements
- Bankruptcies, insolvency patterns, enforcement actions

D) Financial profile and economic footprint
- Source of wealth hypotheses and support
- Asset mapping: real estate, corporate holdings, material assets
- Liabilities, recurring disputes, phoenixing indicators
- Tax compliance and arrears indicators where accessible

E) Reputation and character assessment
- Multi-jurisdiction media review
- Conflict narratives, misconduct allegations, repeated controversy themes
- Affiliations that carry regulatory or reputational risk

F) Online presence and digital footprint
- Social accounts, handles, content risk
- Data-breach indicators and credential exposure risk
- Pattern-of-life and location consistency checks where relevant

RED FLAG SYNTHESIS
- Identify all red flags without minimization.
- For each red flag, link it explicitly to: Verified evidence, Allegation, Inference, and remaining gaps.

OUTPUT FORMAT
You must deliver one of two roles, depending on user instruction:

ROLE (TA-01): Intake & Planning Only
Purpose: Convert intake into a precise investigation plan.
Output must include:
1. Identity Resolution Matrix
2. Disambiguation Risks
3. Jurisdiction & Record Map
4. Investigation Hypotheses
5. Execution Checklist
Do NOT execute research under TA-01.

ROLE (TA-02): Full Investigation Execution
Required structure:
1. Subject Confirmation Summary
2. Legal & Criminal Findings
3. Civil Litigation & Disputes
4. Financial & Economic Footprint
5. Reputation & Character Assessment
6. Online Presence & Digital Footprint
7. Explicit Gaps & Restricted Records
8. Preliminary Red Flag List (no scoring)

EXECUTION GATES
DEPTH VERIFICATION GATE
Minimum final report length: 5,000 words of substantive analytical content. Headings and boilerplate do not count.
If the minimum depth has not been reached, do not conclude. Continue in additional clearly labeled parts.

STRUCTURAL CONTINUITY GATE
If output must be delivered in multiple parts:
- Maintain identical section structure across all parts
- Do not summarize prior parts
- Continue exactly where the previous part ended

START RULE
Default to TA-01 unless the user explicitly requests TA-02.`;

    const trustApplicatorEntity = `You are "Trust Applicator - Entity", an APX-grade Deal & Entity Due Diligence engine.

FILE CONTEXT PURGE & HARD RESET DIRECTIVE (ABSOLUTE)
For every execution, assume a hard reset of file context. Any file that appears attached, visible, cached, preloaded, or retained from a prior run is invalid unless explicitly uploaded for the current request.

PRIMARY PURPOSE
Determine whether APX should proceed with, restructure, or terminate engagement with a deal, entity, or counterparty.

DEEP RESEARCH MODE: ALWAYS ON
Operate at Investment Committee depth. Multi-jurisdiction and multi-language by default.
If you cannot access required public sources directly in this environment, state that limitation and provide a concrete verification path.

CORE ASSUMPTIONS
- Entity first.
- Individuals are analyzed as risk-bearing components of entities and deals.
- Offshore, dissolved, dormant, and shadow entities MUST be included where relevant.
- Absence of information is a material finding.

JURISDICTION DISCIPLINE
- Do NOT assume United States, Delaware, or any jurisdiction by default.
- Infer jurisdictions from incorporation, operating locations, asset locations, banking, litigation, and regulatory references.

EXECUTION STANDARDS
- Maximum lawful depth. No shortcuts.
- Explicitly separate: Verified Facts, Allegations, Inferences, Unknowns / Gaps.
- Do not fabricate records, outcomes, access, or confirmations.
- Do not collapse entities or individuals without confirmation.
- Aggressively surface contradictions, omissions, inconsistencies, and unexplained absences.
- Treat complexity, opacity, and over-structuring as independent risk signals.

DEAL FOCUS (MANDATORY)
Every analysis must explicitly assess:
- Deal structure validity
- Counterparty credibility
- Capital integrity and source-of-funds plausibility
- Regulatory exposure (securities, AML/KYC, consumer, data, industry-specific)
- Reputational contamination and media-risk vectors
- Hidden control, shadow directors, beneficial ownership and influence
- Failure modes, downside scenarios, and unwind paths

SOURCE TRACEABILITY
For every material claim, explicitly state the source type (registry filing, court docket, regulator notice, audited financials, reputable media, or uploaded document name). If a claim is an inference, label it explicitly and explain why.

MINIMUM DEPTH REQUIREMENT
Final output must be a minimum of 8,000 words of substantive analytical content (headings and boilerplate do not count). If limits require continuation, continue in clearly labeled parts with identical structure.

MANDATORY FINAL DETERMINATION
Conclude with a section titled "FINAL APX DETERMINATION" containing:
- Explicit recommendation: PROCEED / RESTRUCTURE / TERMINATE
- Primary drivers of the decision (risk-based)
- Non-negotiable conditions (if any) required to proceed
- Residual risks APX would knowingly accept

STRUCTURE (RECOMMENDED)
1. Entity and Deal Snapshot
2. Identity and Control Map (entities and individuals)
3. Corporate Record Findings (status, filings, addresses, officers)
4. Beneficial Ownership and Hidden Control Hypotheses
5. Financial Condition and Capital Integrity
6. Litigation, Regulatory, and Enforcement Exposure
7. Reputation and Adverse Media
8. Deal Mechanics Review (economic terms, covenants, protections)
9. Failure Modes and Downside Scenarios
10. Explicit Unknowns / Gaps and How to Close Them
11. FINAL APX DETERMINATION`;

    const htmlPresentation = `SINGLE-SCROLL HTML PRESENTATION (STRICT TEMPLATE REPLICATION, ZERO TEXT LOSS, WORD-COUNT VERIFICATION)

FILE CONTEXT PURGE & HARD RESET DIRECTIVE (ABSOLUTE)
For every execution, assume a hard reset of file context. Any file that appears attached, visible, cached, preloaded, or retained due to prior runs is invalid unless it was explicitly uploaded by the user for the current request.

You are given:
1) A design HTML template file (the Design Template) that defines the exact visual identity, layout, typography, spacing, colors, background geometry, watermarks, and UI behaviors, including a search box system.
2) One or more information source files (Word, PDF, TXT, or other text-based documents) that contain the full content that must be rendered into the final presentation.

YOUR JOB
Upon request by the user, produce a single, final, self-contained HTML presentation that:
- Is one continuous, scrollable page (no slide deck, no PowerPoint-style cards, no paginated sections that behave like slides).
- Replicates the Design Template completely and precisely.
- Renders 100% of the source content (all text and information) into the presentation with zero truncation.
- Uses no summaries, no paraphrasing, no shortcuts, and no omissions.
- Does not invent content, does not fill gaps, and does not introduce any independent visual design language outside the Design Template.
- Enforces content completeness via a word-count reconciliation process.

NON-NEGOTIABLE RULES
A) Single scroll page only
- Output must be one long, vertically scrollable HTML page.
- Do not implement slides, pages, stepper flows, carousel navigation, or next/prev slide UX.
- Same-page anchors are allowed only if the template already uses them.

B) Zero text loss
- Every word from every attached info file must appear in the output presentation.
- Do not compress, shorten, summarize, paraphrase, or rewrite.
- Do not exclude headers, footers, captions, footnotes, endnotes, tables, exhibits, annexes, disclaimers, signatures, or repeated clauses.
- If a PDF contains selectable text, extract it faithfully. If any section is unclear, include it under Source Extraction Notes rather than omitting it.

C) Strict design template adoption
- The Design Template is the only design authority.
- Do not create a new look, layout, palette, font system, spacing system, or component library.
- Only modify what is necessary to insert the content into the template’s existing structure.
- If additional sections are needed, duplicate the template’s existing section pattern(s) exactly.

D) Search box system: template only
- The search box system must be the one that already exists in the Design Template.
- Adapt and extend that exact system so it works across the entire inserted content on the single scroll page.
- Do not invent a new search UI or alternate search behavior.

E) Single deliverable
- Output exactly one file: presentation.html
- Must open locally in a browser with no build steps.
- No broken links. No missing assets. Everything required must be included.

F) No hallucinations
- Do not invent facts, names, dates, metrics, claims, or sources.
- If something is not present in the attachments, it must not appear as content.

WORD-COUNT RECONCILIATION (MANDATORY)
1) Source word count (before writing HTML)
- Extract all text from all attached info files into a single Source Master Text in the exact reading order you will render it.
- A word is any token separated by whitespace after normalizing multiple spaces/newlines to a single space.
- Compute TotalWords_Source and WordsPerFile_Source.

2) Output word count (after building HTML)
- From the final presentation.html, derive Visible Rendered Text for counting purposes.
- Count only human-visible content text nodes that are part of the inserted source content.
- Exclude navigation labels, template boilerplate UI text, button labels, repeated footer chrome that exists in the template, and JS/CSS.
- Compute TotalWords_OutputContent.

3) Required match threshold
- TotalWords_OutputContent must be equal to TotalWords_Source, or within AllowedVariance = MAX(0.5% of TotalWords_Source, 50 words).

4) If mismatch occurs
- Do not finalize.
- Locate missing or duplicated content by comparing WordsPerFile_Source to the HTML sections.
- Insert missing text verbatim or remove accidental duplicates until within variance.
- Repeat counts until it passes.

CONTENT INGESTION RULES
- Preserve lists as lists and tables as tables.
- Preserve emphasis when present.
- Preserve meaningful line breaks (addresses, signatures, legal clauses).

QUALITY CONTROL BEFORE FINAL OUTPUT
- Single-page scroll check.
- Completeness check: no truncation, no omissions.
- Search system fidelity.
- Word-count reconciliation check.
- Template fidelity check.

FINAL INSTRUCTIONS
- Produce only the final HTML as your response (presentation.html content).
- No commentary, no explanation, no plan unless explicitly requested.
- If there is any conflict between neatness and completeness, completeness wins.
`;

    const strategicAnalysis = `You are APX Group’s internal Strategy and Investment Advisor.

Operating requirements
- Think like an Investment Committee partner: direct, skeptical, and decision-oriented.
- Do not use marketing language. Do not hedge with generic caveats.
- If information is missing, list it as decision-critical and propose how to obtain it, but still provide a best-effort recommendation based on what is known.

Output format (mandatory)
1) Executive Decision (first)
- Recommendation: PROCEED / PAUSE / RESTRUCTURE / TERMINATE
- One paragraph rationale

2) Situation Snapshot
- What is being evaluated, who the counterparty is, and what APX is being asked to do

3) Investment Thesis
- Why this could win
- Why it could fail

4) Market and Competitive Map
- Market size logic (with explicit assumptions)
- Competitive set and differentiation
- Distribution or go-to-market constraints

5) Business Model and Economics
- Revenue drivers and pricing power
- Cost structure and scalability
- Unit economics logic (even if approximate)

6) Key Risks and Deal-Kill Thresholds
- Legal/regulatory
- Counterparty credibility
- Capital integrity
- Execution capacity
- Reputational contamination
- Technology or operational risk

7) Scenario Analysis and Sensitivities
- Base case, upside, downside
- Identify the 3 variables that move outcomes most

8) Decision Triggers
- Specific facts that would flip the recommendation

9) Structuring Options
- How to reprice, re-scope, or add protections

10) Next Steps
- 7-day plan
- 30-day plan

Sources Used
- List any links or documents the user provided.
`;

    const deepResearch = `You are a senior APX research analyst operating in Deep Research mode.

Mandate
- Produce a multi-angle, multi-source research brief designed to support APX investment decisions.
- Be explicit about uncertainty. Do not guess.

Process
- If absolutely required, ask up to 5 targeted questions. Otherwise proceed immediately.
- Create hypotheses first, then attempt to validate or falsify them.

Output structure (mandatory)
1) Research Question and Scope
2) What We Know (from user and uploads)
3) Key Hypotheses
4) Evidence Map
- For each hypothesis, list: supporting evidence, conflicting evidence, and what is missing
5) Stakeholder Map
- Entities, principals, incentives, conflicts, hidden control risks
6) Timeline and Milestones (if relevant)
7) Risk Register
- Risk, likelihood (low/med/high), impact (low/med/high), mitigations, verification steps
8) Opportunity Map
- Upside paths, leverage points, strategic synergies for APX
9) Verification Plan
- Exact sources to check (registries, court dockets, regulators, audited filings, reputable media archives)
10) Bottom Line for APX
- What can be concluded now, and what cannot

Sources Used
- List links and uploaded document names.
`;

    const reportGeneration = `You are producing an APX internal report suitable for Investment Committee and counsel review.

Requirements
- Tone: professional, legal-grade, concise.
- Do not include inline citation numbers. If sources are referenced, list them only in a final Sources Used section.
- Prefer structured reasoning over narrative.

Output structure (mandatory)
1) Cover Summary
- Title, date, prepared for, prepared by

2) Executive Summary
- Key findings
- Recommendation
- Top 5 risks

3) Scope and Inputs
- What was reviewed (documents, links, interviews)
- What was not available

4) Findings (organized by category)
- Commercial
- Financial
- Legal and regulatory
- Counterparty and governance
- Technical/operational (if relevant)
- Reputational

5) Open Questions and Data Requests
- Specific requests, ranked by decision criticality

6) Risk Register
- Risk, likelihood, impact, mitigation, owner (blank), deadline (blank)

7) Recommended Actions
- Immediate actions
- Conditions precedent
- Monitoring and reporting covenants

8) Appendix
- Assumptions
- Definitions

Sources Used
- List links and uploaded document names.
`;

    const knowledgeBase = `You are APX Knowledge Base.

Purpose
Convert user-provided notes, documents, and transcripts into a clean, searchable knowledge entry that can be reused in future diligence and execution.

Rules
- Facts only in the main summary. Label speculation as inference.
- Preserve exact names, dates, amounts, jurisdictions, and role titles.
- Do not include inline citation numbers.

Output
1) Canonical Summary (facts only)
2) Entities and People
- Name, role, jurisdiction, identifiers, key relationships
3) Key Decisions and Rationales
4) Commitments and Obligations
- Who owes what to whom, amounts, dates, conditions
5) Risks and Dependencies
6) Open Questions and Missing Documents
7) Action Items
- Owner: (blank)
- Deadline: (blank)
- Task:
8) Sources Used
- File names and links provided
`;

    return {
      general: {
        icon: MessageSquare,
        title: 'General Chat',
        blurb: 'General-purpose APX assistant. Clear, direct, business-oriented outputs.',
        prompt: 'You are an expert AI assistant for APX Group specializing in deep research, strategic analysis, diligence, and professional report generation.'
      },
      research: {
        icon: Search,
        title: 'Deep Research',
        blurb: 'Multi-angle investigation, hypothesis testing, source discipline, and explicit uncertainty handling.',
        prompt: deepResearch
      },
      strategy: {
        icon: BarChart3,
        title: 'Strategic Analysis',
        blurb: 'IC-style strategy memos with scenarios, sensitivities, decision triggers, and recommended actions.',
        prompt: strategicAnalysis
      },
      background_private: {
        icon: Shield,
        title: 'Background Checks: Private',
        blurb: 'Exhaustive private individual background investigations (Trust Applicator - Private).',
        prompt: `${researchDiscipline}\n${trustApplicatorPrivate}`
      },
      background_entity: {
        icon: Shield,
        title: 'Background Checks: Entity',
        blurb: 'Entity and deal due diligence (Trust Applicator - Entity), including principals and beneficial ownership risk.',
        prompt: `${researchDiscipline}\n${trustApplicatorEntity}`
      },
      html: {
        icon: Code,
        title: 'HTML Presentations',
        blurb: 'Single-scroll HTML presentation assembly with strict template replication and completeness gates.',
        prompt: `${researchDiscipline}\n${htmlPresentation}`
      },
      report: {
        icon: FileText,
        title: 'Report Generation',
        blurb: 'IC-ready reports with tight structure, quantified reasoning, and explicit assumptions.',
        prompt: reportGeneration
      },
      knowledge: {
        icon: BookOpen,
        title: 'Knowledge Base',
        blurb: 'Convert raw notes and documents into structured entries, entities, open questions, and action items.',
        prompt: knowledgeBase
      }
    };
  }, []);

  const modePlaceholders = useMemo(() => ({
    general: 'Write your request. Add any relevant context, constraints, and desired output format.',
    research: 'State the research question and decision context. Add any sources or links you want included.',
    strategy: 'Describe the decision, objective, constraints, timeline, and what success looks like. Add numbers if you have them.',
    background_private: 'Enter the individual’s name and any known details (aliases, jurisdictions, roles, links). State what you want verified.',
    background_entity: 'Enter the entity or deal name and any known details (jurisdictions, principals, website, documents, proposed terms). State what you want verified.',
    html: 'Upload the design template HTML and the content sources. State any required section order and any strict constraints.',
    report: 'Describe the report type, audience, and required sections. Provide the source materials or key excerpts.',
    knowledge: 'Paste or upload notes and documents to ingest. Specify taxonomy or output format if you have one.'
  }), []);


  useEffect(() => {
    const authStatus = sessionStorage.getItem('apx_authenticated');
    if (authStatus === 'true') setIsAuthenticated(true);
    const savedModel = localStorage.getItem('apx_model');
    const savedTemp = localStorage.getItem('apx_temperature');
    const savedTokens = localStorage.getItem('apx_max_tokens');
    if (savedModel) setModel(savedModel);
    if (savedTemp) setTemperature(parseFloat(savedTemp));
    if (savedTokens) setMaxTokens(parseInt(savedTokens));
  }, []);

  // Load chat history once the user is authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    const list = loadConversations();
    if (list.length === 0) {
      const fresh = createConversation(activeMode);
      const next = [fresh];
      setConversations(next);
      setActiveConversationId(fresh.id);
      setMessages([]);
      persistConversations(next);
      sessionStorage.setItem('apx_active_conversation_id', fresh.id);
      return;
    }

    setConversations(list);
    const lastId = sessionStorage.getItem('apx_active_conversation_id');
    const candidate = lastId && list.find((c) => c.id === lastId) ? lastId : list[0].id;
    setActiveConversationId(candidate);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // When active conversation changes, load its messages
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!activeConversationId) return;
    const conv = conversations.find((c) => c.id === activeConversationId);
    if (!conv) return;
    sessionStorage.setItem('apx_active_conversation_id', conv.id);
    setMessages(Array.isArray(conv.messages) ? conv.messages : []);
    if (conv.modeKey && modes[conv.modeKey]) {
      setActiveMode(conv.modeKey);
    }
  }, [activeConversationId, isAuthenticated, conversations, modes]);

  // Lock body scroll while history drawer is open
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.style.overflow = isHistoryOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isHistoryOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Prevent background scrolling when the history drawer is open
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = isHistoryOpen ? 'hidden' : prev || '';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isHistoryOpen]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [input]);

  useEffect(() => {
    setSystemPrompt(modes[activeMode].prompt);
    // Force background checks into "deep research" posture: max rigor, citations on.
    if (activeMode === 'background_private' || activeMode === 'background_entity') {
      setThinkingMode(true);
      setCitationsEnabled(true);
    }
  }, [activeMode, modes]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsLoggingIn(true);
    try {
      const response = await fetch('/.netlify/functions/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: loginCode })
      });
      const data = await response.json();
      if (data.success) {
        sessionStorage.setItem('apx_authenticated', 'true');
        setIsAuthenticated(true);
        setLoginCode('');
      } else {
        setLoginError('Invalid access code. Please try again.');
      }
    } catch (error) {
      setLoginError('Connection error. Please try again.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('apx_authenticated');
    setIsAuthenticated(false);
    setMessages([]);
    setConversationHistory([]);
  };

  const saveSettings = () => {
    localStorage.setItem('apx_model', model);
    localStorage.setItem('apx_temperature', temperature.toString());
    localStorage.setItem('apx_max_tokens', maxTokens.toString());
    setShowConfigurations(false);
  };

  const openConfiguration = () => {
    // Snapshot current settings so we can revert if user closes without saving
    configSnapshotRef.current = {
      model,
      thinkingMode,
      citationsEnabled,
      temperature,
      maxTokens
    };
    setShowConfigurations(true);
  };

  const cancelConfiguration = () => {
    const snap = configSnapshotRef.current;
    if (snap) {
      setModel(snap.model);
      setThinkingMode(snap.thinkingMode);
      setCitationsEnabled(snap.citationsEnabled);
      setTemperature(snap.temperature);
      setMaxTokens(snap.maxTokens);
    }
    setShowConfigurations(false);
  };

  const toggleConfiguration = () => {
    if (showConfigurations) {
      cancelConfiguration();
    } else {
      openConfiguration();
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;
    // If for any reason there's no active conversation yet, create one.
    let convId = activeConversationId;
    if (!activeConversationId) {
      const fresh = createConversation(activeMode);
      const nextList = [fresh, ...(conversations || [])];
      setConversations(nextList);
      persistConversations(nextList);
      convId = fresh.id;
      setActiveConversationId(fresh.id);
    }

    const userMessage = { role: 'user', content: input.trim(), mode: activeMode, timestamp: new Date().toISOString() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    saveActiveConversation(newMessages, activeMode, convId);
    setInput('');
    setIsLoading(true);
    const conversationMessages = [...conversationHistory, { role: 'user', content: userMessage.content }];
    try {
      const response = await fetch('/.netlify/functions/claude', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: conversationMessages, model, temperature, maxTokens, systemPrompt })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'API request failed');
      }
      const data = await response.json();
      const assistantMessage = { role: 'assistant', content: data.content[0].text, id: data.id, mode: activeMode, timestamp: new Date().toISOString() };
      const finalMessages = [...newMessages, assistantMessage];
      setMessages(finalMessages);
      saveActiveConversation(finalMessages, activeMode, convId);
      setConversationHistory([...conversationMessages, { role: 'assistant', content: data.content[0].text }]);
    } catch (error) {
      const errMsg = { role: 'error', content: 'Error: ' + error.message, mode: activeMode, timestamp: new Date().toISOString() };
      const finalMessages = [...newMessages, errMsg];
      setMessages(finalMessages);
      saveActiveConversation(finalMessages, activeMode, convId);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const clearChat = () => {
    if (window.confirm('Clear all messages?')) {
      setMessages([]);
      setConversationHistory([]);
      saveActiveConversation([], activeMode);
    }
  };

  const newChat = () => {
    const fresh = createConversation(activeMode);
    const nextList = [fresh, ...(conversations || [])];
    setConversations(nextList);
    persistConversations(nextList);
    setActiveConversationId(fresh.id);
    setMessages([]);
    setConversationHistory([]);

    // Clear composer + close any panels so the new chat state is obvious.
    setInput('');
    setIsHistoryOpen(false);
    setShowConfigurations(false);

    // Focus the composer for immediate typing.
    requestAnimationFrame(() => {
      if (textareaRef?.current) textareaRef.current.focus();
    });
  };

  const openConversation = (conversationId) => {
    if (!conversationId) return;
    setActiveConversationId(conversationId);
    sessionStorage.setItem('apx_active_conversation_id', conversationId);
    setIsHistoryOpen(false);
  };

  const deleteConversation = (conversationId) => {
    if (!conversationId) return;
    if (!window.confirm('Delete this conversation?')) return;

    setConversations((prev) => {
      const filtered = (prev || []).filter((c) => c.id !== conversationId);
      const next = filtered.length ? filtered : [createConversation(activeMode)];
      persistConversations(next);

      // If the active conversation was deleted, switch to the most recent one.
      if (activeConversationId === conversationId) {
        const replacementId = next[0].id;
        setActiveConversationId(replacementId);
        setMessages(next[0].messages || []);
        setConversationHistory([]);
        sessionStorage.setItem('apx_active_conversation_id', replacementId);
      }
      return next;
    });
  };

if (!isAuthenticated) {
    return (
      <div style={{ minHeight: '100vh', background: apxDark, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif', position: 'relative' }}>
        <div style={{ position: 'fixed', inset: 0, zIndex: 0, opacity: 0.08, pointerEvents: 'none' }}>
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="diagonals" x="0" y="0" width="300" height="300" patternUnits="userSpaceOnUse">
                <line x1="0" y1="0" x2="300" y2="300" stroke="white" strokeWidth="2" opacity="0.5" />
                <line x1="-100" y1="100" x2="200" y2="400" stroke="white" strokeWidth="1.5" opacity="0.3" />
                <line x1="100" y1="-100" x2="400" y2="200" stroke="white" strokeWidth="2.5" opacity="0.4" />
                <line x1="50" y1="0" x2="250" y2="300" stroke="white" strokeWidth="1" opacity="0.25" />
                <line x1="150" y1="0" x2="350" y2="200" stroke="white" strokeWidth="1.8" opacity="0.35" />
                <line x1="-50" y1="150" x2="150" y2="350" stroke="white" strokeWidth="1.2" opacity="0.3" />
                <line x1="200" y1="-50" x2="400" y2="150" stroke="white" strokeWidth="1.5" opacity="0.28" />
                <line x1="0" y1="200" x2="200" y2="400" stroke="white" strokeWidth="1.3" opacity="0.32" />
                <line x1="250" y1="50" x2="450" y2="250" stroke="white" strokeWidth="1.6" opacity="0.27" />
                <polyline points="0,50 150,200 300,50" stroke="white" strokeWidth="1" fill="none" opacity="0.2" />
                <polyline points="50,0 200,150 50,300" stroke="white" strokeWidth="1.2" fill="none" opacity="0.22" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#diagonals)" />
          </svg>
        </div>
        <div style={{ maxWidth: '448px', width: '100%', position: 'relative', zIndex: 1 }}>
          <div style={{ background: 'rgba(26, 26, 26, 0.8)', backdropFilter: 'blur(20px)', borderRadius: '16px', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)', border: '1px solid rgba(201, 169, 97, 0.3)', padding: '48px 32px' }}>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div
              style={{
                width: 108,
                height: 108,
                borderRadius: 28,
                border: '1px solid rgba(201, 169, 97, 0.22)',
                background: 'rgba(0,0,0,0.22)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.35) inset, 0 16px 44px rgba(0,0,0,0.45), 0 0 34px rgba(201, 169, 97, 0.28)',
                margin: '0 auto 24px',
                overflow: 'visible'
              }}
            >
              <img
                src="/logo.png"
                alt="APX Group Logo"
                style={{
                  width: '72%',
                  height: '72%',
                  objectFit: 'contain',
                  display: 'block',
                  filter: 'drop-shadow(0 8px 18px rgba(201, 169, 97, 0.32))'
                }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div style={{ display: 'none', width: '72%', height: '72%', alignItems: 'center', justifyContent: 'center', color: '#c9a961' }}>APX</div>
            </div>
              <div style={{ width: '80px', height: '80px', background: 'linear-gradient(135deg, ' + apxGold + ' 0%, #a08344 100%)', borderRadius: '12px', display: 'none', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', boxShadow: '0 4px 16px rgba(201, 169, 97, 0.3)' }}>
                <Lock className="w-10 h-10" style={{ color: apxDark }} />
              </div>
              <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: apxGold, marginBottom: '8px', letterSpacing: '0.5px' }}>APX GROUP</h1>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>AI Intelligence Platform</p>
            </div>
            <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: apxGold }}>Access Code</label>
                <input type="password" value={loginCode} onChange={(e) => { setLoginCode(e.target.value); setLoginError(''); }} placeholder="Enter your access code" disabled={isLoggingIn} autoFocus style={{ width: '100%', padding: '12px 16px', borderRadius: '999px', background: 'rgba(26, 26, 26, 0.6)', border: '1px solid rgba(201, 169, 97, 0.3)', color: '#ffffff', fontSize: '16px', textAlign: 'center', letterSpacing: '3px', outline: 'none' }} />
              </div>
              {loginError && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: 'rgba(220, 38, 38, 0.1)', border: '1px solid rgba(220, 38, 38, 0.3)', borderRadius: '8px' }}>
                  <AlertCircle className="w-5 h-5 text-red-400" />
                  <p style={{ fontSize: '14px', color: '#fca5a5' }}>{loginError}</p>
                </div>
              )}
              <button type="submit" disabled={isLoggingIn || !loginCode} style={{ width: '100%', background: 'linear-gradient(135deg, ' + apxGold + ' 0%, #a08344 100%)', color: apxDark, padding: '14px 24px', borderRadius: '999px', fontWeight: 'bold', fontSize: '16px', border: 'none', cursor: isLoggingIn || !loginCode ? 'not-allowed' : 'pointer', opacity: isLoggingIn || !loginCode ? 0.5 : 1, boxShadow: '0 4px 16px rgba(201, 169, 97, 0.3)' }}>
                {isLoggingIn ? <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}><Loader2 className="w-5 h-5 animate-spin" />Authenticating...</span> : 'Access Platform'}
              </button>
            </form>
            <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid rgba(201, 169, 97, 0.2)' }}>
              <p style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center' }}>Secure access for authorized personnel only</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const ModeIcon = modes[activeMode].icon;

  return (
    <div style={{ minHeight: '100vh', background: apxDark, color: '#ffffff', fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, opacity: 0.08, pointerEvents: 'none' }}>
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="diagonals" x="0" y="0" width="300" height="300" patternUnits="userSpaceOnUse">
              <line x1="0" y1="0" x2="300" y2="300" stroke="white" strokeWidth="2" opacity="0.5" />
              <line x1="-100" y1="100" x2="200" y2="400" stroke="white" strokeWidth="1.5" opacity="0.3" />
              <line x1="100" y1="-100" x2="400" y2="200" stroke="white" strokeWidth="2.5" opacity="0.4" />
              <line x1="50" y1="0" x2="250" y2="300" stroke="white" strokeWidth="1" opacity="0.25" />
              <line x1="150" y1="0" x2="350" y2="200" stroke="white" strokeWidth="1.8" opacity="0.35" />
              <line x1="-50" y1="150" x2="150" y2="350" stroke="white" strokeWidth="1.2" opacity="0.3" />
              <line x1="200" y1="-50" x2="400" y2="150" stroke="white" strokeWidth="1.5" opacity="0.28" />
              <line x1="0" y1="200" x2="200" y2="400" stroke="white" strokeWidth="1.3" opacity="0.32" />
              <line x1="250" y1="50" x2="450" y2="250" stroke="white" strokeWidth="1.6" opacity="0.27" />
              <polyline points="0,50 150,200 300,50" stroke="white" strokeWidth="1" fill="none" opacity="0.2" />
              <polyline points="50,0 200,150 50,300" stroke="white" strokeWidth="1.2" fill="none" opacity="0.22" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#diagonals)" />
        </svg>
      </div>
      <div style={{ position: 'relative', zIndex: 1 }}>
        <header className="apx-header" style={{ background: 'rgba(26, 26, 26, 0.8)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(201, 169, 97, 0.3)', position: 'sticky', top: 0, zIndex: 10, boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)' }}>
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-4">
              <div
                className="flex items-center gap-3"
                onClick={() => setActiveMode('general')}
                title="Return to General Chat"
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 16,
                  border: '1px solid rgba(201, 169, 97, 0.28)',
                  background: 'rgba(0,0,0,0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 0 0 1px rgba(0,0,0,0.35) inset, 0 10px 28px rgba(0,0,0,0.35), 0 0 22px rgba(201, 169, 97, 0.35)',
                  flex: '0 0 auto',
                  overflow: 'visible'
                }}
              >
                <img
                  src="/logo.png"
                  alt="APX Group Logo"
                  style={{
                    width: '70%',
                    height: '70%',
                    objectFit: 'contain',
                    display: 'block',
                    filter: 'drop-shadow(0 4px 12px rgba(201, 169, 97, 0.35))'
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <div style={{ display: 'none', width: '70%', height: '70%', alignItems: 'center', justifyContent: 'center', color: '#c9a961' }}>APX</div>
              </div>
                <div className="flex items-center justify-center" style={{ width: '48px', height: '48px', background: 'linear-gradient(135deg, ' + apxGold + ' 0%, #a08344 100%)', borderRadius: '12px', boxShadow: '0 4px 12px rgba(201, 169, 97, 0.6)', display: 'none' }}>
                  <Sparkles className="w-6 h-6 text-black" />
                </div>
                <div>
                  <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: apxGold, letterSpacing: '0.5px' }}>APX GROUP</h1>
                  <p style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>AI Intelligence Platform</p>
                </div>
              </div>
              <div className="flex gap-2">
                {[
                  { icon: Plus, action: newChat, title: 'New chat' },
                  { icon: MessageSquare, action: () => setIsHistoryOpen(true), title: 'History' },
                  { icon: Trash2, action: clearChat, title: 'Clear chat' },
                  { icon: LogOut, action: handleLogout, title: 'Logout' }
                ].map((btn, i) => {
                  const Icon = btn.icon;
                  return (
                    <button key={i} onClick={btn.action} title={btn.title} style={{ padding: '10px', borderRadius: '8px', background: 'rgba(26, 26, 26, 0.6)', backdropFilter: 'blur(10px)', border: '1px solid rgba(201, 169, 97, 0.3)', color: apxGold, cursor: 'pointer', transition: 'all 0.3s ease' }} onMouseEnter={(e) => e.currentTarget.style.background = apxGold + '20'} onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(26, 26, 26, 0.6)'}>
                      <Icon className="w-5 h-5" />
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="apx-modegrid">
              {Object.entries(modes)
                .filter(([key]) => key !== 'general')
                .map(([key, mode]) => {
                const Icon = mode.icon;
                const isActive = activeMode === key;
                return (
                  <button key={key} onClick={() => setActiveMode(key)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', borderRadius: '24px', background: isActive ? 'linear-gradient(135deg, ' + apxGold + ' 0%, #a08344 100%)' : 'rgba(26, 26, 26, 0.6)', backdropFilter: 'blur(10px)', border: '1px solid ' + (isActive ? apxGold : apxGold + '30'), color: isActive ? '#000' : apxGold, fontWeight: isActive ? 'bold' : 'normal', cursor: 'pointer', boxShadow: isActive ? '0 4px 12px rgba(201, 169, 97, 0.6)' : 'none', transition: 'all 0.3s ease' }} onMouseEnter={(e) => !isActive && (e.currentTarget.style.background = apxGold + '20')} onMouseLeave={(e) => !isActive && (e.currentTarget.style.background = 'rgba(26, 26, 26, 0.6)')}>
                    <Icon className="w-4 h-4" style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: '14px' }}>{mode.title}</span>
                  </button>
                );
              })}
              <button onClick={toggleConfiguration} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', borderRadius: '24px', background: showConfigurations ? 'linear-gradient(135deg, ' + apxGold + ' 0%, #a08344 100%)' : 'rgba(26, 26, 26, 0.6)', backdropFilter: 'blur(10px)', border: '1px solid ' + (showConfigurations ? apxGold : apxGold + '30'), color: showConfigurations ? '#000' : apxGold, fontWeight: showConfigurations ? 'bold' : 'normal', cursor: 'pointer', boxShadow: showConfigurations ? '0 4px 12px rgba(201, 169, 97, 0.6)' : 'none', transition: 'all 0.3s ease' }} onMouseEnter={(e) => !showConfigurations && (e.currentTarget.style.background = apxGold + '20')} onMouseLeave={(e) => !showConfigurations && (e.currentTarget.style.background = 'rgba(26, 26, 26, 0.6)')}>
                <Settings className="w-4 h-4" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: '14px' }}>Configuration</span>
              </button>
            </div>
          </div>
        </header>
        {isHistoryOpen && (
          <div className="apx-history-overlay" onClick={() => setIsHistoryOpen(false)}>
            <div className="apx-history-panel" onClick={(e) => e.stopPropagation()}>
              <div className="apx-history-header">
                <div className="apx-history-title">Chat History</div>
                <button className="apx-history-close" onClick={() => setIsHistoryOpen(false)} aria-label="Close history">✕</button>
              </div>

              <div className="apx-history-actions">
                <button className="apx-history-new" onClick={() => { newChat(); setIsHistoryOpen(false); }}>New chat</button>
              </div>

              <div className="apx-history-list">
                {(conversations || []).length === 0 ? (
                  <div className="apx-history-empty">No saved conversations yet.</div>
                ) : (
                  ([...(conversations || [])].sort((a, b) => (b?.updatedAt || 0) - (a?.updatedAt || 0))).map((c) => {
                    const isActive = c.id === activeConversationId;
                    const modeLabel = (modes[c.modeKey]?.label) || 'General Chat';
                    const ts = c.updatedAt || c.createdAt;
                    const when = ts ? new Date(ts).toLocaleString() : '';
                    return (
                      <div key={c.id} className={"apx-history-item" + (isActive ? ' is-active' : '')}>
                        <button className="apx-history-item-main" onClick={() => openConversation(c.id)}>
                          <div className="apx-history-item-title">{c.title || 'New Conversation'}</div>
                          <div className="apx-history-item-meta">
                            <span className="apx-history-item-mode">{modeLabel}</span>
                            <span className="apx-history-item-dot">·</span>
                            <span className="apx-history-item-time">{when}</span>
                          </div>
                        </button>
                        <button className="apx-history-item-del" onClick={() => deleteConversation(c.id)} aria-label="Delete conversation">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}

{showConfigurations && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' }}>
            <div style={{ position: 'relative', background: 'rgba(26, 26, 26, 0.95)', backdropFilter: 'blur(20px)', borderRadius: '16px', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)', maxWidth: '672px', width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: '32px', border: '1px solid rgba(201, 169, 97, 0.3)' }}>
              <button
                onClick={cancelConfiguration}
                aria-label="Close without saving"
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  background: 'rgba(26, 26, 26, 0.6)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(201, 169, 97, 0.35)',
                  color: apxGold,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
              <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '24px', color: apxGold }}>Configuration</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: apxGold }}>Model Selection</label>
                  <select value={model} onChange={(e) => setModel(e.target.value)} style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', background: 'rgba(26, 26, 26, 0.6)', backdropFilter: 'blur(10px)', border: '1px solid rgba(201, 169, 97, 0.3)', color: '#ffffff', outline: 'none', cursor: 'pointer', fontSize: '14px' }}>
                    <optgroup label="Claude 4 (Latest)" style={{ background: '#1a1a1a' }}>
                      <option value="claude-sonnet-4-20250514">Claude Sonnet 4 (Recommended)</option>
                      <option value="claude-opus-4-20250514">Claude Opus 4 (Most Capable)</option>
                      <option value="claude-haiku-4-20250514">Claude Haiku 4 (Fastest)</option>
                    </optgroup>
                    <optgroup label="Claude 3.5" style={{ background: '#1a1a1a' }}>
                      <option value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet (Latest)</option>
                      <option value="claude-3-5-sonnet-20240620">Claude 3.5 Sonnet (June 2024)</option>
                      <option value="claude-3-5-haiku-20241022">Claude 3.5 Haiku</option>
                    </optgroup>
                    <optgroup label="Claude 3" style={{ background: '#1a1a1a' }}>
                      <option value="claude-3-opus-20240229">Claude 3 Opus</option>
                      <option value="claude-3-sonnet-20240229">Claude 3 Sonnet</option>
                      <option value="claude-3-haiku-20240307">Claude 3 Haiku</option>
                    </optgroup>
                  </select>
                  <p style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '6px' }}>Sonnet: Balance • Opus: Intelligence • Haiku: Speed</p>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: apxGold }}>Features</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', padding: '12px', background: 'rgba(26, 26, 26, 0.4)', borderRadius: '8px', border: '1px solid rgba(201, 169, 97, 0.2)' }}>
                      <input type="checkbox" checked={thinkingMode} onChange={(e) => setThinkingMode(e.target.checked)} style={{ width: '18px', height: '18px', accentColor: apxGold, cursor: 'pointer' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', color: '#ffffff', fontSize: '14px' }}>Extended Thinking</div>
                        <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>Let Claude think longer for complex tasks</div>
                      </div>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', padding: '12px', background: 'rgba(26, 26, 26, 0.4)', borderRadius: '8px', border: '1px solid rgba(201, 169, 97, 0.2)' }}>
                      <input type="checkbox" checked={citationsEnabled} onChange={(e) => setCitationsEnabled(e.target.checked)} style={{ width: '18px', height: '18px', accentColor: apxGold, cursor: 'pointer' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', color: '#ffffff', fontSize: '14px' }}>Citations</div>
                        <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>Include source citations in responses</div>
                      </div>
                    </label>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: apxGold }}>Temperature: {temperature.toFixed(1)}</label>
                  <input type="range" min="0" max="1" step="0.1" value={temperature} onChange={(e) => setTemperature(parseFloat(e.target.value))} style={{ width: '100%', accentColor: apxGold }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px' }}>
                    <span>Precise</span>
                    <span>Balanced</span>
                    <span>Creative</span>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: apxGold }}>Max Output Tokens: {maxTokens.toLocaleString()}</label>
                  <input type="range" min="1024" max="8192" step="1024" value={maxTokens} onChange={(e) => setMaxTokens(parseInt(e.target.value))} style={{ width: '100%', accentColor: apxGold }} />
                  <p style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px' }}>Controls response length (higher = longer responses)</p>
                </div>
                <div style={{ padding: '16px', background: 'rgba(201, 169, 97, 0.15)', border: '1px solid rgba(201, 169, 97, 0.3)', borderRadius: '8px' }}>
                  <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.8)', lineHeight: '1.5' }}>
                    <strong style={{ color: apxGold }}>Current Configuration:</strong><br/>
                    Model: {model.includes('sonnet-4') ? 'Sonnet 4' : model.includes('opus-4') ? 'Opus 4' : model.includes('haiku-4') ? 'Haiku 4' : model.includes('3-5') ? 'Claude 3.5' : 'Claude 3'}<br/>
                    Extended Thinking: {thinkingMode ? 'ON' : 'OFF'}<br/>
                    Citations: {citationsEnabled ? 'ON' : 'OFF'}
                  </p>
                </div>
              </div>
              <div className="flex gap-3 mt-8">
                <button onClick={saveSettings} style={{ flex: 1, background: 'linear-gradient(135deg, ' + apxGold + ' 0%, #a08344 100%)', color: '#000', padding: '14px 24px', borderRadius: '8px', fontWeight: 'bold', border: 'none', cursor: 'pointer', boxShadow: '0 4px 16px rgba(201, 169, 97, 0.6)' }}>Save & Close</button>
              </div>
            </div>
          </div>
        )}
        <main className="max-w-6xl mx-auto px-4 py-8 pb-40">
          {messages.length === 0 ? (
            <div className="text-center py-16">
              <div className="mx-auto mb-8 flex items-center justify-center" style={{ width: '96px', height: '96px', background: 'linear-gradient(135deg, ' + apxGold + ' 0%, #a08344 100%)', borderRadius: '24px', boxShadow: '0 8px 24px rgba(201, 169, 97, 0.3)' }}>
                <ModeIcon className="w-12 h-12" style={{ color: apxDark }} />
              </div>
              <h2 style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '16px', color: apxGold }}>{modes[activeMode].title}</h2>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '48px', maxWidth: '672px', marginLeft: 'auto', marginRight: 'auto', fontSize: '18px' }}>{modes[activeMode].blurb}</p>
              {/* Removed the "Select a mode above to begin" helper line for a cleaner mode header */}
            </div>
          ) : (
            <div className="space-y-6">
              {messages.map((msg, idx) => {
                const isUser = msg.role === 'user';
                const isError = msg.role === 'error';
                return (
                  <div key={idx} className={'flex gap-4 ' + (isUser ? 'justify-end' : 'justify-start')}>
                    <div className={'max-w-4xl ' + (isUser ? 'order-2' : '')}>
                      <div style={{ padding: '24px', borderRadius: '12px', boxShadow: isUser ? '0 4px 16px rgba(201, 169, 97, 0.4)' : '0 4px 16px rgba(0, 0, 0, 0.3)', background: isUser ? 'linear-gradient(135deg, ' + apxGold + ' 0%, #a08344 100%)' : isError ? 'rgba(220, 38, 38, 0.1)' : 'rgba(26, 26, 26, 0.8)', backdropFilter: 'blur(20px)', border: '1px solid ' + (isUser ? apxGold : isError ? 'rgba(220, 38, 38, 0.3)' : 'rgba(201, 169, 97, 0.3)'), color: isUser ? '#000' : isError ? '#fca5a5' : '#ffffff' }}>
                        {isError && (
                          <div className="flex items-start gap-2 mb-3">
                            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                            <span style={{ fontWeight: '600', color: '#fca5a5' }}>Error</span>
                          </div>
                        )}
                        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '1.6' }}>{msg.content}</div>
                        {msg.role === 'assistant' && (
                          <div className="flex gap-2 mt-4 pt-4" style={{ borderTop: '1px solid rgba(201, 169, 97, 0.2)' }}>
                            <button onClick={() => copyToClipboard(msg.content, idx)} style={{ padding: '8px', borderRadius: '8px', background: 'rgba(26, 26, 26, 0.6)', backdropFilter: 'blur(10px)', border: '1px solid rgba(201, 169, 97, 0.3)', color: apxGold, cursor: 'pointer' }}>
                              {copiedId === idx ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {isLoading && (
                <div className="flex gap-4">
                  <div style={{ background: 'rgba(26, 26, 26, 0.8)', backdropFilter: 'blur(20px)', border: '1px solid rgba(201, 169, 97, 0.3)', borderRadius: '12px', padding: '24px', maxWidth: '1024px' }}>
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-5 h-5 animate-spin" style={{ color: apxGold }} />
                      <span style={{ color: apxGold }}>Analyzing...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </main>
        <div className="apx-composer" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, borderTop: '1px solid rgba(201, 169, 97, 0.3)', backdropFilter: 'blur(20px)', background: 'rgba(26, 26, 26, 0.9)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
          <div className="max-w-6xl mx-auto px-4 py-6">
            <div style={{ background: 'rgba(26, 26, 26, 0.6)', backdropFilter: 'blur(20px)', border: '1px solid rgba(201, 169, 97, 0.3)', borderRadius: '28px', overflow: 'hidden', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)' }}>
              <textarea className="apx-textarea" ref={textareaRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} placeholder={modePlaceholders[activeMode] || ('Ask anything in ' + modes[activeMode].title + ' mode...')} disabled={isLoading} style={{ width: '100%', padding: '16px 24px', background: 'transparent', outline: 'none', resize: 'none', color: '#ffffff', border: 'none' }} rows="1" />
              <div className="px-6 pb-4 flex items-center justify-between">
                <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', padding: '8px 12px', background: 'rgba(26, 26, 26, 0.6)', backdropFilter: 'blur(10px)', borderRadius: '999px', border: '1px solid rgba(201, 169, 97, 0.3)' }}>
                  {model === 'claude-sonnet-4-20250514' ? 'Sonnet 4' : model === 'claude-opus-4-20250514' ? 'Opus 4' : model.includes('haiku-4') ? 'Haiku 4' : model.includes('3-5') ? 'Claude 3.5' : 'Claude 3'}
                </span>
                <button onClick={sendMessage} disabled={!input.trim() || isLoading} style={{ background: 'linear-gradient(135deg, ' + apxGold + ' 0%, #a08344 100%)', color: '#000', padding: '12px', borderRadius: '999px', border: 'none', cursor: !input.trim() || isLoading ? 'not-allowed' : 'pointer', opacity: !input.trim() || isLoading ? 0.5 : 1, boxShadow: '0 4px 12px rgba(201, 169, 97, 0.6)', fontWeight: 'bold' }}>
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APXClaudeChat;